import { IOptionProps } from "@blueprintjs/core";
import { getIn } from "formik";
import {
  ButtonsBloc,
  FGCustomPanel,
  FGListen,
  FGSelectInput,
  FGTextAreaInput,
  FGTextInput,
  FieldGroup,
  FieldSet,
  InlineButtonContainer,
} from "nsitools-react";
import * as React from "react";
import { useHistory } from "react-router";
import * as Yup from "yup";

import {
  AffixRequestApi,
  EPlatform,
  ERequestState,
  NewKennelRequestDto,
  PersonApi,
  RequestApi,
  RequestDto,
  RequestInfoDto,
} from "../../../../../api";
import {
  CancelButton,
  FGPeopleSearchInput,
  FGSRSHDateMaskInput,
  SharepointButton,
  SmallFormGenerator,
} from "../../../../../components";
import { useAuth, useDialog, useEventsContext } from "../../../../../contexts";
import { ERoutes } from "../../../../../ERoutes";
import { useApiService, useCrudApi, useTl, useWindow } from "../../../../../hooks";
import { useReferential } from "../../../../../hooks/useReferential";
import { ETLCodes } from "../../../../../locales";
import { RequestBreedTransientSelection } from "../../../request/item/RequestBreedTransientSelection";
import { RequestTransientPersonSelection } from "../../../request/item/RequestTransientPersonSelection";

export interface INewKennelRequestDetailProps {
  newKennelRequestId: number;
  editMode: boolean;
  requestInfo: RequestInfoDto;
}

export const NewKennelRequestDetail: React.FunctionComponent<INewKennelRequestDetailProps> = ({
  newKennelRequestId,
  editMode,
}) => {
  const { t } = useTl();
  const history = useHistory();
  const api = useApiService(AffixRequestApi);
  const { user } = useAuth();
  const personApi = useApiService(PersonApi);
  const { showDialogPromise } = useDialog();
  const { openNewTab } = useWindow();

  const [ownerSelectorErrors, setownerSelectorErrors] = React.useState("");
  const [kennelTypes, ktloading] = useReferential((a) => a.getKennelTypes(), false, []);

  const [ownerIdsBeforeEdition, setOwnerIdsBeforeEdition] = React.useState([]);
  const [breedIdsBeforeEdition, setBreedIdsBeforeEdition] = React.useState([]);

  // =========================== DATA MANIPULATION ==================================================

  const { data, loading, saving, saveItem, refresh } = useCrudApi<NewKennelRequestDto>(
    React.useMemo(
      () => ({
        getApiFn: (id = newKennelRequestId) =>
          id > 0
            ? api.getNewKennelRequest(id)
            : new NewKennelRequestDto({
                transientBreedIds: [],
                transientOwnerIds: [],
                request: new RequestDto({
                  status: ERequestState.REQUESTED,
                  remark: "",
                  requestDate: new Date(),
                }),
              }).toJSON(),
        saveApiFn: (d: NewKennelRequestDto) => {
          const id = api.saveNewKennelRequest(d);
          const updatedOwnerIds = [...ownerIdsBeforeEdition];
          d.transientOwnerIds?.forEach((ownerId) => {
            if (!updatedOwnerIds.includes(ownerId)) {
              updatedOwnerIds.push(ownerId);
            }
          });
          const updatedBreedIds = [...breedIdsBeforeEdition];
          d.transientBreedIds?.forEach((breedId) => {
            if (!updatedBreedIds.includes(breedId)) {
              updatedBreedIds.push(breedId);
            }
          });
          setOwnerIdsBeforeEdition(updatedOwnerIds);
          setBreedIdsBeforeEdition(updatedBreedIds);
          return id;
        },
        onSavedRoute: (d) => `${ERoutes.affixRequest}/${d.newKennelRequestId}/detail/edit/newKennel`,
      }),
      [api, breedIdsBeforeEdition, newKennelRequestId, ownerIdsBeforeEdition]
    ),
    [newKennelRequestId]
  );

  const generateNameOfferList = React.useCallback((formik) => {
    const names = [];
    for (let index = 1; index < 6; index++) {
      const element = getIn(formik?.values, `nameOffer${index}`);
      if (element) {
        names.push(element);
      }
    }
    return names?.map((p) => {
      return { value: p, label: p };
    }) as IOptionProps[];
  }, []);

  const computeError = React.useCallback(
    (ids) => {
      if (
        user.platform === EPlatform.CARE4DOGS &&
        (newKennelRequestId <= 0 || data?.request?.status === ERequestState.REQUESTED)
      ) {
        ids.forEach(async (element) => {
          const result = await personApi.isPersonBelgian(element);
          if (!result.value) {
            setownerSelectorErrors(t(ETLCodes.OneOrMoreOwnersNotBelgian));
            return;
          }
        });
        ids.forEach(async (element) => {
          const result = await personApi.isKennelOwner(element, 0, new Date(), null);
          if (result.value) {
            setownerSelectorErrors(t(ETLCodes.OneOrMorPersonAlreadyOwnsAKennel));
            return;
          }
        });
        setownerSelectorErrors("");
        return;
      }
    },
    [data, newKennelRequestId, personApi, t]
  );

  const [saveChecking, setSaveChecking] = React.useState(false);
  const saveCheck = React.useCallback(
    async (values) => {
      setSaveChecking(true);
      if (ownerSelectorErrors !== "") {
        return;
      }

      let isBanned = false;
      if (newKennelRequestId <= 0) {
        isBanned = (await personApi.isPersonExcludedQuery(values?.request?.personId)).value;
      }
      if (isBanned) {
        const isbannedValidation = await showDialogPromise({
          message: t(ETLCodes.PersonIsBannedConfirmMessage),
          title: t(ETLCodes.Warning),
        });
        if (isbannedValidation !== "yes") return;
      }
      setSaveChecking(false);
      await saveItem(values);
    },
    [newKennelRequestId, ownerSelectorErrors, personApi, saveItem, showDialogPromise, t]
  );

  // =========================== EVENT MANAGER ==================================================
  const onRequestRefreshEvent = React.useCallback(() => {
    refresh();
  }, [refresh]);

  const { subscribeToEvent, unsubscribeEvent } = useEventsContext();
  React.useEffect(() => {
    subscribeToEvent("RequestRefresh", onRequestRefreshEvent);
    return () => unsubscribeEvent("RequestRefresh", onRequestRefreshEvent);
  }, [onRequestRefreshEvent, subscribeToEvent, unsubscribeEvent]);

  // =========================== FORM ==================================================
  const RequestedFormSchema = React.useMemo(() => {
    return Yup.object().shape({
      nameOffer1: Yup.string().nullable().required(t(ETLCodes.Required)),
      nameOffer1Type: Yup.string().nullable().required(t(ETLCodes.Required)),
      finalName: Yup.string()
        .nullable()
        .test("is-required", t(ETLCodes.Required), function (value) {
          if (data?.request?.status === ERequestState.TO_EVALUATE_FCI) {
            return !!value;
          }
          return true;
        }),
      fciNumber: Yup.string()
        .nullable()
        .test("is-required", t(ETLCodes.Required), function (value) {
          if (data?.request?.status === ERequestState.TO_EVALUATE_FCI) {
            return !!value;
          }
          return true;
        }),
      transientOwnerIds: Yup.array().of(
        Yup.number().test("is-begian", t(ETLCodes.AtLeastOneOwnerIsNotBelgian), async function (value) {
          if (newKennelRequestId <= 0 || data?.request?.status === ERequestState.REQUESTED) {
            const result = await personApi.isPersonBelgian(value);
            return result.value;
          }
          return true;
        })
      ),
      request: Yup.object().shape({
        personId: Yup.number()
          .nullable()
          .required(t(ETLCodes.Required))
          .test("is-not-already-owner", t(ETLCodes.PersonIsAlreadyKennelOwner), async function (value) {
            if (newKennelRequestId <= 0 || data?.request?.status === ERequestState.REQUESTED) {
              const result = await api.isPersonKennelOwnerQuery(value);
              return !result.value;
            }
            return true;
          })
          .test("missing-adress-information", t(ETLCodes.MissingCompleteAdressOrEmail), async function (value) {
            if (newKennelRequestId <= 0 || data?.request?.status === ERequestState.REQUESTED) {
              if (value !== 0 && value !== null && value !== undefined) {
                const result = await personApi.missingAddressInformationCheck(value);
                return result.value;
              }
              return true;
            }
            return true;
          })
          .test("is-begian", t(ETLCodes.IsNotBelgian), async function (value) {
            if (newKennelRequestId <= 0 || data?.request?.status === ERequestState.REQUESTED) {
              const result = await personApi.isPersonBelgian(value);
              return result.value;
            }
            return true;
          }),
        requestDate: Yup.date().nullable().required(t(ETLCodes.Required)),
      }),
    });
  }, [api, data, newKennelRequestId, personApi, t]);

  const OtherFormSchema = React.useMemo(() => {
    return Yup.object().shape({});
  }, []);

  const statusEditable = React.useMemo(() => {
    if (data) {
      return (
        data?.request?.status === ERequestState.REQUESTED ||
        data?.request?.status === ERequestState.AWAITING_APPLICANT_REACTION ||
        data?.request?.status === ERequestState.BILLED ||
        data?.request?.status === ERequestState.PAID
      );
    }
    return false;
  }, [data]);

  const requestApi = useApiService(RequestApi);
  const [shpLoading, setShpLoading] = React.useState(false);
  const existsInShp = React.useMemo(() => !!data?.request.sharepointUri, [data?.request]);
  const openOrCreateInShp = React.useCallback(async () => {
    setShpLoading(true);
    if (!!data?.request.sharepointUri) {
      window.open(data?.request.sharepointUri, "_blank");
      setShpLoading(false);
    } else {
      try {
        await requestApi.createInSharepoint(data?.requestId);
        refresh();
      } catch (e) {
        throw e;
      } finally {
        setShpLoading(false);
      }
    }
  }, [data?.request.sharepointUri, data?.requestId, requestApi, refresh]);

  return (
    <>
      <SmallFormGenerator
        initialValues={data}
        editMode={editMode}
        onSubmit={saveCheck}
        validationSchema={data?.request?.status === ERequestState.REQUESTED ? RequestedFormSchema : OtherFormSchema}
        loading={loading}
        onCancel={() => history.push(ERoutes.affixRequest)}
        saving={saving || saveChecking}
        hideButtons={data?.request?.status === ERequestState.EXECUTED}
      >
        <FGListen field="transientOwnerIds" onChanged={computeError} />
        <FieldSet
          title={t(ETLCodes.NewKennelRequest)}
          rightElement={
            newKennelRequestId > 0 && (
              <SharepointButton existsInShp={existsInShp} loading={shpLoading} onClick={openOrCreateInShp} />
            )
          }
        >
          <FieldGroup columns={2}>
            <div>
              <FGSRSHDateMaskInput
                name="request.requestDate"
                label={t(ETLCodes.RequestDate)}
                requiredMark
                readonly={!statusEditable || data?.request?.isBlocked}
              />
              <FGPeopleSearchInput
                name="request.personId"
                label={t(ETLCodes.Contact)}
                onItemViewClick={(item) => openNewTab(`${ERoutes.people}/${item}/detail/view`)}
                readonly={!statusEditable || data?.request?.isBlocked}
                requiredMark
                showWarning={true}
              />
            </div>
            <FGTextAreaInput label={t(ETLCodes.Remark)} name="request.remark" growVertically rows={6} />
          </FieldGroup>
        </FieldSet>

        <FieldSet title={t(ETLCodes.FileAffix)}>
          <FieldGroup columns={2}>
            <FGTextInput
              name="nameOffer1"
              trim={true}
              label={t(ETLCodes.NameOffer1)}
              readonly={!statusEditable || data?.request?.isBlocked}
              maxLength={29}
              forceCase={"upper"}
            />
            <FGSelectInput
              name="nameOffer1Type"
              label={t(ETLCodes.Affix)}
              items={kennelTypes}
              loading={ktloading}
              readonly={!statusEditable || data?.request?.isBlocked}
            />
            <FGTextInput
              name="nameOffer2"
              trim={true}
              label={t(ETLCodes.NameOffer2)}
              readonly={!statusEditable || data?.request?.isBlocked}
              maxLength={29}
              forceCase={"upper"}
            />
            <FGSelectInput
              name="nameOffer2Type"
              label={t(ETLCodes.Affix)}
              items={kennelTypes}
              loading={ktloading}
              readonly={!statusEditable || data?.request?.isBlocked}
            />
            <FGTextInput
              name="nameOffer3"
              trim={true}
              label={t(ETLCodes.NameOffer3)}
              readonly={!statusEditable || data?.request?.isBlocked}
              maxLength={29}
              forceCase={"upper"}
            />
            <FGSelectInput
              name="nameOffer3Type"
              label={t(ETLCodes.Affix)}
              items={kennelTypes}
              loading={ktloading}
              readonly={!statusEditable || data?.request?.isBlocked}
            />
            <FGTextInput
              name="nameOffer4"
              trim={true}
              label={t(ETLCodes.NameOffer4)}
              readonly={!statusEditable || data?.request?.isBlocked}
              maxLength={29}
              forceCase={"upper"}
            />
            <FGSelectInput
              name="nameOffer4Type"
              label={t(ETLCodes.Affix)}
              items={kennelTypes}
              loading={ktloading}
              readonly={!statusEditable || data?.request?.isBlocked}
            />
            <FGTextInput
              name="nameOffer5"
              trim={true}
              label={t(ETLCodes.NameOffer5)}
              readonly={!statusEditable || data?.request?.isBlocked}
              maxLength={29}
              forceCase={"upper"}
            />
            <FGSelectInput
              name="nameOffer5Type"
              label={t(ETLCodes.Affix)}
              items={kennelTypes}
              loading={ktloading}
              readonly={!statusEditable || data?.request?.isBlocked}
            />
          </FieldGroup>
        </FieldSet>
        {user.platform === EPlatform.PORTAL ? null : (
          <FieldSet title={t(ETLCodes.Owners)}>
            <FGCustomPanel>
              {({ formik }) => (
                <>
                  <RequestTransientPersonSelection
                    linkedIds={formik?.values?.transientOwnerIds || null}
                    onLinkedIdsChanged={(ids) => formik?.setFieldValue("transientOwnerIds", ids)}
                    editMode={editMode && !data?.request?.isBlocked && statusEditable}
                    loading={loading}
                    errors={ownerSelectorErrors}
                  />
                </>
              )}
            </FGCustomPanel>
          </FieldSet>
        )}
        <FieldSet title={t(ETLCodes.Breeds)}>
          <FGCustomPanel>
            {({ formik }) => (
              <>
                <RequestBreedTransientSelection
                  linkedIds={formik?.values?.transientBreedIds || null}
                  onLinkedIdsChanged={(ids) => formik?.setFieldValue("transientBreedIds", ids)}
                  editMode={editMode && !data?.request?.isBlocked && statusEditable}
                  loading={loading}
                />
              </>
            )}
          </FGCustomPanel>
        </FieldSet>
        {(data?.request?.status === ERequestState.TO_EVALUATE_FCI ||
          data?.request?.status === ERequestState.EXECUTED) && (
          <FieldSet title={t(ETLCodes.FCIDecision)}>
            <FGCustomPanel>
              {(ctx) => {
                return (
                  <FGSelectInput
                    name="finalName"
                    label={t(ETLCodes.FinalName)}
                    items={generateNameOfferList(ctx.formik)}
                    loading={ktloading}
                    readonly={data?.request?.status === ERequestState.EXECUTED}
                  />
                );
              }}
            </FGCustomPanel>
            <FGTextInput name="finalType" label={t(ETLCodes.FinalAffix)} />
            <FGTextInput
              name="fciNumber"
              label={t(ETLCodes.FciNumber)}
              readonly={data?.request?.status === ERequestState.EXECUTED}
            />
          </FieldSet>
        )}
        {data?.request?.status === ERequestState.EXECUTED && (
          <InlineButtonContainer>
            <div />
            <ButtonsBloc style={{ float: "right" }}>
              <CancelButton minimal={false} onClick={() => history.push(`${ERoutes.affixRequest}`)} />
            </ButtonsBloc>
          </InlineButtonContainer>
        )}
      </SmallFormGenerator>
    </>
  );
};
